'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { User } from '@supabase/supabase-js';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, Menu, X } from 'lucide-react';

export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<User | null>(null);
  const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const supabase = createClientComponentClient();

  useEffect(() => {
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event: string, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, [supabase.auth]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      router.push('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out. Please try again.');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link
                href={pathname === '/' ? '/' : '/candidate'}
                className="flex items-center mr-4"
              >
                <Image
                  src="/cruit-eu-stars.svg"
                  alt="Logo"
                  width={32}
                  height={32}
                />
                <div className="flex">
                  <span className="ml-2 text-xl font-bold">Cruit</span>
                  <span className="text-xs text-gray-500 ml-1 mb-1 self-end">
                    {pathname === '/' ? 'For Companies' : 'For Candidates'}
                  </span>
                </div>
              </Link>

              <div className="hidden md:block">
                {pathname === '/' ? (
                  <Link href="/candidate">
                    <Button
                      variant="outline"
                      className="text-black dark:text-white hover:bg-gray-100 dark:bg-black dark:hover:bg-gray-600"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      For Candidates
                    </Button>
                  </Link>
                ) : (
                  <Link href="/">
                    <Button
                      variant="outline"
                      className="text-black dark:text-white hover:bg-gray-100 dark:bg-black dark:hover:bg-gray-600"
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      For Companies
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-4">
              {pathname === '/' ? (
                user ? (
                  <>
                    <Link href="/dashboard">
                      <Button variant="ghost">Dashboard</Button>
                    </Link>
                    <Button onClick={handleSignOut} variant="outline">
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link href="/login">
                      <Button variant="outline">Login</Button>
                    </Link>
                    <Link href="/signup?role=company">
                      <Button>Start hiring</Button>
                    </Link>
                  </>
                )
              ) : (
                <>
                  <Link href="/login">
                    <Button variant="outline">Login</Button>
                  </Link>
                  <Link
                    href={
                      pathname.includes('companies')
                        ? '/signup?role=company'
                        : '/signup?role=candidate'
                    }
                  >
                    <Button>Create account</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="flex md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded={isMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg">
              {pathname === '/' ? (
                <Link
                  href="/candidate"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  For Candidates
                </Link>
              ) : (
                <Link
                  href="/"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                >
                  For Companies
                </Link>
              )}

              {user ? (
                <>
                  <Link
                    href="/dashboard"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    Login
                  </Link>
                  <Link
                    href={
                      pathname === '/'
                        ? '/signup?role=company'
                        : pathname.includes('companies')
                          ? '/signup?role=company'
                          : '/signup?role=candidate'
                    }
                    className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                  >
                    {pathname === '/' ? 'Start hiring' : 'Create account'}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {showUnsupportedModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Unsupported Browser</h2>
            <p>Your browser doesn&apos;t support this authentication method.</p>
            <button onClick={() => setShowUnsupportedModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
